import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import ContasService from "services/accounts";
import FinanceiroService from "services/financeiro";
import NaturezaService from "services/sub-group-accounts";
import { hojeISO as hoje } from "utils/dataCalendario";

export const TIPOS_LANCAMENTO = {
  SALDO: "Saldo informado do banco",
  E: "Entrada manual",
  S: "Saída manual",
};

const vazio = (padrao = {}) => ({
  account: "",
  tipo: "SALDO",
  data: hoje(),
  valor: "",
  historico: "",
  natureza: "",
  ...padrao,
});

/**
 * Cria/edita lançamento bancário: saldo informado (ponto de partida da
 * movimentação) ou entrada/saída manual (tarifa, transferência...).
 */
export default function LancamentoDialog({
  open,
  lancamento,
  padrao,
  onClose,
  onSalvo,
}) {
  const [valores, setValores] = useState(vazio());
  const [contas, setContas] = useState([]);
  const [naturezas, setNaturezas] = useState([]);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    ContasService.list()
      .then(({ data }) => setContas(data || []))
      .catch(() => {});
    NaturezaService.list()
      .then(({ data }) => setNaturezas(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setErro("");
    setValores(
      lancamento
        ? {
            account: lancamento.account?._id || lancamento.account || "",
            tipo: lancamento.tipo,
            data: new Date(lancamento.data).toISOString().slice(0, 10),
            valor: String(lancamento.valor).replace(".", ","),
            historico: lancamento.historico || "",
            natureza: lancamento.natureza?._id || lancamento.natureza || "",
          }
        : vazio(padrao)
    );
  }, [open, lancamento]);

  const alterar = (campo) => (e) =>
    setValores((v) => ({ ...v, [campo]: e.target.value }));

  const salvar = async () => {
    if (!valores.account) return setErro("Escolha a conta bancária.");
    if (valores.valor === "") return setErro("Informe o valor.");
    setSalvando(true);
    setErro("");
    try {
      if (lancamento?._id)
        await FinanceiroService.atualizarLancamento(lancamento._id, valores);
      else await FinanceiroService.criarLancamento(valores);
      onSalvo?.();
    } catch (e) {
      setErro(e?.error || e?.message || "Erro ao salvar o lançamento.");
    }
    setSalvando(false);
  };

  const select = {
    select: true,
    SelectProps: { native: true },
    InputLabelProps: { shrink: true },
    fullWidth: true,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 10000 }}
    >
      <DialogTitle>
        {lancamento?._id ? "Editar lançamento" : "Novo lançamento"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Conta bancária"
              value={valores.account}
              onChange={alterar("account")}
              {...select}
            >
              <option value=""></option>
              {contas.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.nome}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Tipo"
              value={valores.tipo}
              onChange={alterar("tipo")}
              {...select}
            >
              {Object.entries(TIPOS_LANCAMENTO).map(([v, rotulo]) => (
                <option key={v} value={v}>
                  {rotulo}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={valores.tipo === "SALDO" ? "Saldo no fim do dia" : "Data"}
              type="date"
              value={valores.data}
              onChange={alterar("data")}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Valor (R$)"
              value={valores.valor}
              onChange={(e) =>
                setValores((v) => ({
                  ...v,
                  valor: e.target.value.replace(
                    v.tipo === "SALDO" ? /[^\d,-]/g : /[^\d,]/g,
                    ""
                  ),
                }))
              }
              helperText={valores.tipo === "SALDO" ? "Pode ser negativo" : ""}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Histórico"
              value={valores.historico}
              onChange={alterar("historico")}
              fullWidth
            />
          </Grid>
          {valores.tipo !== "SALDO" && (
            <Grid item xs={12}>
              <TextField
                label="Natureza de lançamento"
                value={valores.natureza}
                onChange={alterar("natureza")}
                {...select}
              >
                <option value=""></option>
                {naturezas.map((n) => (
                  <option key={n._id} value={n._id}>
                    {n.codigo ? `${n.codigo} - ` : ""}
                    {n.nome}
                  </option>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          sx={{ color: "white !important" }}
          disabled={salvando}
          onClick={salvar}
        >
          {salvando ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
